import {
  Component,
  contentChild,
  inject,
  input,
  TemplateRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { TabStripComponent } from './tab-strip.component';

/**
 * Individual tab component for use within TabStripComponent.
 *
 * @example Basic usage with text label
 * ```html
 * <app-tab id="overview" label="Overview">
 *   <h2>Overview Content</h2>
 *   <p>Your panel content here...</p>
 * </app-tab>
 * ```
 *
 * @example Advanced usage with custom tab label (icons, badges, etc.)
 * ```html
 * <app-tab id="settings" label="Settings">
 *   <ng-template #tabLabel>
 *     <i class="icon-gear"></i>
 *     <span>Settings</span>
 *     @if (hasChanges) {
 *       <span class="badge">!</span>
 *     }
 *   </ng-template>
 *
 *   <!-- Panel content (default ng-content) -->
 *   <app-settings-form />
 * </app-tab>
 * ```
 *
 * @remarks
 * - The `id` must be unique within the tab strip
 * - The `label` is used as fallback text if no `#tabLabel` template is provided
 * - Use `#tabLabel` template for custom tab button content (icons, badges, etc.)
 * - Default ng-content is used for the tab panel content
 * - This component has no visual representation (display: contents)
 */
@Component({
  selector: 'app-tab',
  standalone: true,
  host: {
    '[class.tab-panel--active]': 'isActive()',
  },
  template: `
    <div
      class="tab-panel"
      [class.tab-panel--active]="isActive()"
      role="tabpanel"
      [attr.id]="'panel-' + id()"
      [attr.aria-labelledby]="'tab-' + id()"
      [attr.data-testid]="'panel-' + id()"
    >
      <ng-content />
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [':host { display: contents; }'],
})
export class TabComponent {
  private tabStrip = inject(TabStripComponent, { optional: true });

  /**
   * Unique identifier for this tab.
   * Used to match tabs with panels and for selection state.
   */
  id = input.required<string>();

  /**
   * Fallback text label shown in the tab button when no #tabLabel template is provided.
   * @default ''
   */
  label = input<string>('');

  /**
   * Optional custom template for the tab button content.
   * Reference this template with `#tabLabel` in the component content.
   *
   * @example
   * ```html
   * <app-tab id="overview" label="Overview">
   *   <ng-template #tabLabel>
   *     <i class="icon-home"></i>
   *     <span>Home</span>
   *   </ng-template>
   *   <!-- panel content -->
   * </app-tab>
   * ```
   */
  tabLabelTemplate = contentChild('tabLabel', { read: TemplateRef });

  /**
   * Check if this tab is currently active
   */
  isActive(): boolean {
    if (!this.tabStrip) return false;
    const selectedId = this.tabStrip.selected();
    return selectedId === this.id();
  }
}
